import { alpha, createTheme } from '@mui/material/styles';

const brandColors = {
    primary: '#D3A424',
    primaryAlt: '#D3A40D',
    secondary: '#FFFBF0',
    error: '#E30000',
    success: '#1EB435',
    warning: '#EBA739',
    black: '#1C1C1C',
    substitutionMode: '#F3DD29',
};

const emptyTheme = createTheme({
    palette: {
        common: {
            black: brandColors.black,
            errorBg: '#FFF2F2',
            primaryAlt: brandColors.primaryAlt,
        },
        primary: {
            main: brandColors.primary,
        },
        secondary: {
            main: brandColors.secondary,
        },
        warning: {
            main: brandColors.warning,
        },
        error: {
            main: brandColors.error,
        },
        success: {
            main: brandColors.success,
        },
        default: {
            main: '#bdbdbd', // theme.palette.grey[400]
            dark: '#C6C6C6',
        },
        substitutionMode: {
            main: brandColors.substitutionMode,
        },
        background: {
            default: '#fff',
        },
        grey: {
            100: '#f4f4f4',
            600: '#767676',
            700: '#626262',
            A700: '#C6C6C6',
        },
    },
});

// our custom theme
// docs can be found here: https://mui.com/material-ui/customization/default-theme/
const theme = createTheme({
    ...emptyTheme,
    typography: {
        fontFamily: 'Inter, sans-serif',
        h2: {
            fontSize: 40,
            color: brandColors.black,
        },
        subtitle1: {
            fontSize: 18,
            lineHeight: '30px',
        },
    },
    constants: {
        tunnelInputWidth: 377,
        appBarHeight: 90,
        phaseBarHeight: 56,
        footerHeight: 154,
        sidebarWidth: 248,
        colorCritical: '#B72424',
        colorWarning: brandColors.warning,
        colorSuccess: brandColors.success,
    },
    breakpoints: {
        keys: [...emptyTheme.breakpoints.keys, 'tablet'],
        values: { ...emptyTheme.breakpoints.values, tablet: 1400 },
    },
    components: {
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: 16,
                    padding: '16px 10px',
                },
                body: {
                    paddingTop: '25px',
                    paddingBottom: '25px',
                    color: '#626262',
                    overflow: 'hidden',
                },
                head: {
                    fontWeight: 600,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    padding: 0,
                    borderRadius: 3,
                    backgroundColor: emptyTheme.palette.common.white,
                    '&.Mui-error': {
                        backgroundColor: emptyTheme.palette.common.errorBg,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: alpha(
                            emptyTheme.palette.default.main,
                            emptyTheme.palette.action.disabledBackground
                        ),
                    },
                },
                input: {
                    padding: '8px 18px',
                    fontSize: '14px',
                    lineHeight: 1.75,
                    boxSizing: 'border-box',
                    height: 40,
                },
                multiline: {
                    height: 'auto',
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        padding: 0,
                        '& .MuiAutocomplete-input': {
                            padding: '8px 18px',
                        },
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    '&.Mui-error': {
                        marginLeft: 0,
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                icon: {
                    top: 'calc(50% - 14px)',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    padding: '8px 16px',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 60,
                    fontSize: 13,
                    letterSpacing: 1.3,
                },
                sizeSmall: {
                    fontSize: 12,
                },
                sizeMedium: {
                    fontSize: 13,
                },
                textPrimary: {
                    padding: '6px 10px',
                    background: 'transparent',
                    '&:hover': {
                        background: 'transparent',
                    },
                },
                containedPrimary: {
                    color: '#fff',
                },
                containedSecondary: {
                    color: brandColors.paper,
                },
            },
            variants: [
                {
                    props: { variant: 'contained', color: 'default' },
                    style: {
                        color: emptyTheme.palette.common.white,
                        '&.Mui-disabled': {
                            backgroundColor: alpha(
                                emptyTheme.palette.default.main,
                                emptyTheme.palette.action.disabledOpacity
                            ),
                        },
                        '&:hover': {
                            backgroundColor: emptyTheme.palette.default.dark,
                        },
                    },
                },
                {
                    props: { variant: 'outlined', color: 'default' },
                    style: {
                        color: emptyTheme.palette.grey[500],
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        '&.Mui-disabled': {
                            color: emptyTheme.palette.grey[300],
                            border: `1px solid ${emptyTheme.palette.action.disabledBackground}`,
                        },
                        '&:hover': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            backgroundColor: alpha(
                                emptyTheme.palette.default.main,
                                emptyTheme.palette.action.hoverOpacity
                            ),
                        },
                    },
                },
                {
                    props: { color: 'default', variant: 'text' },
                    style: {
                        color: emptyTheme.palette.grey[500],
                        '&.Mui-disabled': {
                            color: emptyTheme.palette.grey[300],
                        },
                        '&:hover': {
                            color: emptyTheme.palette.common.black,
                            backgroundColor: 'inherit',
                        },
                    },
                },
            ],
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: 16,
                    letterSpacing: 1.6,
                },
                outlined: {
                    transform: 'translate(18px, 12px) scale(1)',
                    '&$shrink': {
                        transform: 'translate(15px, -6px) scale(0.8)',
                        background: 'white',
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: 16,
                    display: 'flex',
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: { fontSize: 16 },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    letterSpacing: 1.6,

                    '&$focused': {
                        marginBottom: 5,
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        backgroundColor: '#F8F8F8',
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    minWidth: 400,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: 12,
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                '#root': {
                    height: '100vh',
                    overflow: 'hidden',
                },
                '*': {
                    fontFamily: 'Inter, sans-serif',
                },
                'a, a:hover, a:visited, a:active, a:focus': {
                    // color: 'inherit',
                },

                /* Chrome, Safari, Edge, Opera */
                'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                },

                /* Firefox */
                'input[type=number]': {
                    MozAppearance: 'textfield',
                },

                '::WebkitFontSmoothing': 'antialiased',
                '::MozOsxFontSmoothing': 'grayscale',

                /* width & height(for horizontal scroll) */
                '::-webkit-scrollbar': {
                    width: 9,
                    height: 9,
                    backgroundColor: '#F5F5F5',
                },

                /* Track */
                '::-webkit-scrollbar-track': {
                    webkitBoxShadow: 'inset 0 0 5px rgba(0,0,0,0.3)',
                    backgroundColor: '#F5F5F5',
                    borderRadius: 5,
                },

                /* Handle */
                '::-webkit-scrollbar-thumb': {
                    borderRadius: 5,
                    background: 'rgba(211,164,36,0.85)',
                },

                /* Handle on hover */
                '::-webkit-scrollbar-thumb:hover': {
                    background: brandColors.primary,
                    opacity: 1,
                },

                /* Apex Charts */
                '.apexcharts-tooltip': {
                    color: '#000 !important',
                },

                // React Quill
                'div.quill': { marginBottom: '16px' },
                'div.ql-container': {
                    height: '250px !important',
                },
                'div.ql-toolbar': {
                    borderRadius: '3px 3px 0 0',
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                'div.quill.small div.ql-container': {
                    height: '150px !important',
                    borderRadius: '0 0 3px 3px',
                    borderColor: 'rgba(0, 0, 0, 0.23)',
                },
                '.quill .ql-snow.ql-toolbar button:hover, .ql-snow .ql-toolbar button:hover, .ql-snow.ql-toolbar button:focus, .ql-snow .ql-toolbar button:focus, .ql-snow.ql-toolbar button.ql-active, .ql-snow .ql-toolbar button.ql-active, .ql-snow.ql-toolbar .ql-picker-label:hover, .ql-snow .ql-toolbar .ql-picker-label:hover, .ql-snow.ql-toolbar .ql-picker-label.ql-active, .ql-snow .ql-toolbar .ql-picker-label.ql-active, .ql-snow.ql-toolbar .ql-picker-item:hover, .ql-snow .ql-toolbar .ql-picker-item:hover, .ql-snow.ql-toolbar .ql-picker-item.ql-selected, .ql-snow .ql-toolbar .ql-picker-item.ql-selected':
                    {
                        color: `${emptyTheme.palette.primary.main} !important`,
                    },
                '.ql-snow.ql-toolbar button:hover .ql-stroke, .ql-snow .ql-toolbar button:hover .ql-stroke, .ql-snow.ql-toolbar button:focus .ql-stroke, .ql-snow .ql-toolbar button:focus .ql-stroke, .ql-snow.ql-toolbar button.ql-active .ql-stroke, .ql-snow .ql-toolbar button.ql-active .ql-stroke, .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke, .ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke, .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke, .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke, .ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke, .ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke, .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke, .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke, .ql-snow.ql-toolbar button:hover .ql-stroke-miter, .ql-snow .ql-toolbar button:hover .ql-stroke-miter, .ql-snow.ql-toolbar button:focus .ql-stroke-miter, .ql-snow .ql-toolbar button:focus .ql-stroke-miter, .ql-snow.ql-toolbar button.ql-active .ql-stroke-miter, .ql-snow .ql-toolbar button.ql-active .ql-stroke-miter, .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke-miter, .ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke-miter, .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter, .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter, .ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke-miter, .ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke-miter, .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter, .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter':
                    {
                        stroke: `${emptyTheme.palette.primary.main} !important`,
                    },

                /* Yousign iframe */
                '#yousign-iframe': { width: '100%', height: '100%' },

                /* Carousel */
                '.carousel-root': {
                    flex: '1 1',
                    width: '100%',
                    background: emptyTheme.palette.common.white,
                },
                '.carousel-slider': {
                    height: '100%',
                },
                '.carousel .slider-wrapper': { height: '100%' },
                'ul.slider, li.slide': {
                    height: '100%',
                },
            },
        },
    },
});

export default theme;
